// MembershipRefresh.tsx — the "Already a member? Refresh" control shared by the three
// unlock walls (FeatureLockWall, CalmTrialLock, SignUpWall).
//
// It exists because those walls used to fire the entitlement re-check and render
// NOTHING about it. The check is a network read, so nothing moved while it ran; an
// account that still had no membership looked identical when it finished; and a
// membership that HAD landed left the wall sitting on top of the now-unlocked app
// (only the close chevron ever cleared it). Every outcome read as a dead button.
//
// So the tap is always answered: busy while it reads, a plain line when there is
// still no membership, and on success a word of confirmation before the wall closes
// itself. Copy follows SHOW_PURCHASE — native has no purchasing surface, so it can
// only ever be "you joined on the website, pick it up here".
import React from 'react';
import { Body } from '@/components/ui';
import { SHOW_PURCHASE, SHOW_IAP } from '@/config/supabase';

/** How long the "you're in" line stays up before the wall closes itself. Long enough
 *  to read, short enough that nobody wonders whether it worked. */
const UNLOCKED_DWELL_MS = 1400;

export function MembershipRefresh({
  onRefresh,
  onUnlocked,
}: {
  /** Re-read entitlement now. Resolves true when the account HAS membership. */
  onRefresh: () => Promise<boolean>;
  /** Membership landed and has been acknowledged on screen: close the wall. */
  onUnlocked: () => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [outcome, setOutcome] = React.useState<'none' | 'member' | null>(null);

  // Held in a ref: the walls pass their parent's `onClose`, which is a fresh closure
  // on every App render. As an effect dependency it would restart this timer on each
  // re-render and the wall could sit there unlocked but open.
  const unlockedRef = React.useRef(onUnlocked);
  unlockedRef.current = onUnlocked;
  React.useEffect(() => {
    if (outcome !== 'member') return;
    const t = setTimeout(() => unlockedRef.current(), UNLOCKED_DWELL_MS);
    return () => clearTimeout(t);
  }, [outcome]);

  const check = async () => {
    if (busy) return; // an impatient second tap must not fire a second read
    setBusy(true);
    setOutcome(null);
    try {
      setOutcome((await onRefresh()) ? 'member' : 'none');
    } catch {
      // Offline, or a read that never landed. The next step is the same either way,
      // so say the same thing rather than inventing a second failure state.
      setOutcome('none');
    } finally {
      setBusy(false);
    }
  };

  const label = busy
    ? 'Checking…'
    : SHOW_PURCHASE
      ? 'I’ve purchased — check again'
      : SHOW_IAP
        ? 'Already a member? Check again'
        : 'Already a member? Refresh';

  return (
    <>
      <button
        onClick={check}
        aria-busy={busy}
        style={{
          width: '100%',
          marginTop: 12,
          background: 'none',
          border: 'none',
          color: 'var(--ink-soft)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 14,
          minHeight: 44,
          opacity: busy ? 0.6 : 1,
          cursor: busy ? 'default' : 'pointer',
        }}
      >
        {label}
      </button>
      {outcome && (
        <Body role="status" size={13.5} style={{ margin: '4px auto 0', maxWidth: 300, lineHeight: 1.5, opacity: 0.8 }}>
          {outcome === 'member'
            ? 'You’re a member. Everything’s unlocked.'
            : SHOW_PURCHASE
              ? 'We couldn’t find a membership on this account yet. If you’ve just paid, give it a minute and try again.'
              : SHOW_IAP
                ? 'We couldn’t find a membership on this account yet. If you’ve just subscribed, give it a minute and try again.'
                : 'We couldn’t find a membership on this account yet. If you’ve just joined on the website, give it a minute and try again.'}
        </Body>
      )}
    </>
  );
}
